import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, XCircle, X, Upload } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import useLicenseStatus from '../../hooks/useLicenseStatus';
import { useLanguage } from '../../contexts/LanguageContext';

/**
 * ON-13 — License Banner
 *
 * Renders null when rung is VALID or still loading.
 * - EXPIRING_SOON  → amber, dismissible
 * - EXPIRED_IN_GRACE → amber, non-dismissible
 * - PAST_GRACE     → red, non-dismissible, role=alert, always-on CTA
 *
 * Design DNA:
 *  - Emerald/slate palette; amber/red for urgency (no gray-*)
 *  - Logical props (ms-*, me-*) for RTL
 *  - No window.alert / window.confirm
 */

const RUNG_EXPIRING = 'EXPIRING_SOON';
const RUNG_GRACE    = 'EXPIRED_IN_GRACE';
const RUNG_PAST     = 'PAST_GRACE';

function BannerShell({ variant, role, children }) {
  const base = 'w-full px-4 py-3 flex items-start gap-3 text-sm';
  const styles = {
    amber: `${base} bg-amber-50 border-b border-amber-200 text-amber-900`,
    red:   `${base} bg-red-50 border-b border-red-200 text-red-900`,
  };
  return (
    <div className={styles[variant] || styles.amber} role={role || 'status'}>
      {children}
    </div>
  );
}

function IconSlot({ variant }) {
  const cls = {
    amber: 'text-amber-600 shrink-0 mt-0.5',
    red:   'text-red-600 shrink-0 mt-0.5',
  };
  if (variant === 'red') {
    return <XCircle size={18} className={cls.red} aria-hidden="true" />;
  }
  return <AlertTriangle size={18} className={cls.amber} aria-hidden="true" />;
}

export default function LicenseBanner() {
  const { t } = useTranslation('license');
  const { isRTL } = useLanguage();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, refetch } = useLicenseStatus();
  const rung = data?.rung ?? null;
  const daysLeft = data?.days_left ?? null;

  const [dismissed, setDismissed] = useState(false);

  // Listen for the license:read-only event dispatched by httpClient when a
  // 403 + LICENSE_READONLY response arrives. Bust the cache so the banner
  // immediately reflects the tighter state without waiting for the 5-min poll.
  useEffect(() => {
    const handler = () => {
      queryClient.invalidateQueries({ queryKey: ['license', 'status'] });
      refetch();
    };
    window.addEventListener('license:read-only', handler);
    return () => window.removeEventListener('license:read-only', handler);
  }, [queryClient, refetch]);

  // Reset dismissal when the rung changes (e.g. license was uploaded)
  useEffect(() => {
    setDismissed(false);
  }, [rung]);

  const handleUpload = useCallback(() => {
    navigate('/settings', { state: { openTab: 'license' } });
  }, [navigate]);

  // VALID, null (loading / unknown) — render nothing
  if (!rung || rung === 'VALID' || rung === 'LOCKOUT') return null;

  // Dismissible: EXPIRING_SOON only
  if (dismissed && rung === RUNG_EXPIRING) return null;

  // ---------- PAST_GRACE — red non-dismissible ----------
  if (rung === RUNG_PAST) {
    return (
      <BannerShell variant="red" role="alert">
        <IconSlot variant="red" />
        <div className="flex-1 min-w-0" dir={isRTL ? 'rtl' : 'ltr'}>
          <span className="font-semibold me-1">{t('license.readOnlyTitle')}.</span>
          <span className="opacity-90">{t('license.readOnlyBody')}</span>
        </div>
        <button
          type="button"
          onClick={handleUpload}
          className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-700 text-white text-xs font-semibold hover:bg-red-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-600 focus-visible:ring-offset-1 transition-colors whitespace-nowrap"
          aria-label={t('license.uploadLicense')}
        >
          <Upload size={13} aria-hidden="true" />
          {t('license.uploadLicense')}
        </button>
      </BannerShell>
    );
  }

  // ---------- EXPIRED_IN_GRACE — amber non-dismissible ----------
  if (rung === RUNG_GRACE) {
    return (
      <BannerShell variant="amber" role="status">
        <IconSlot variant="amber" />
        <div className="flex-1 min-w-0" dir={isRTL ? 'rtl' : 'ltr'}>
          <span className="font-semibold me-1">{t('license.graceTitle')}.</span>
          <span className="opacity-90">{t('license.graceBody')}</span>
        </div>
        <button
          type="button"
          onClick={handleUpload}
          className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-700 text-white text-xs font-semibold hover:bg-amber-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-600 focus-visible:ring-offset-1 transition-colors whitespace-nowrap"
          aria-label={t('license.uploadLicense')}
        >
          <Upload size={13} aria-hidden="true" />
          {t('license.uploadLicense')}
        </button>
      </BannerShell>
    );
  }

  // ---------- EXPIRING_SOON — amber dismissible ----------
  if (rung === RUNG_EXPIRING) {
    const countKey = daysLeft === 1 ? 'license.expiresInDays' : 'license.expiresInDays_plural';
    const body = typeof daysLeft === 'number'
      ? t(countKey, { count: daysLeft })
      : t('license.expiringBody', { count: daysLeft ?? '?' });

    return (
      <BannerShell variant="amber" role="status">
        <IconSlot variant="amber" />
        <div className="flex-1 min-w-0" dir={isRTL ? 'rtl' : 'ltr'}>
          <span className="font-semibold me-1">{t('license.expiringTitle')}.</span>
          <span className="opacity-90">{body}</span>
        </div>
        <div className="shrink-0 flex items-center gap-2">
          <button
            type="button"
            onClick={handleUpload}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-700 text-white text-xs font-semibold hover:bg-amber-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-600 focus-visible:ring-offset-1 transition-colors whitespace-nowrap"
            aria-label={t('license.renewNow')}
          >
            <Upload size={13} aria-hidden="true" />
            {t('license.renewNow')}
          </button>
          <button
            type="button"
            onClick={() => setDismissed(true)}
            aria-label={t('license.dismiss')}
            className="text-amber-700 hover:text-amber-900 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 rounded"
          >
            <X size={18} />
          </button>
        </div>
      </BannerShell>
    );
  }

  return null;
}
